import { css } from '@emotion/core';
import { connect } from 'react-redux';
import { getTopTenCourses } from 'redux/actions/license';
import { EnrollmentPeriod } from 'redux/schemas/models/license';
import moment from 'moment';
import { useEffect } from 'react';
import { getCourseNamesToRankingData, CourseRankingData } from 'redux/selectors/licenses';
import { RootState } from 'redux/schemas';
import LoadingWrapper from 'shared/components/loading-wrapper';
import { loadCoursesForInstitution } from 'redux/actions/institutions';
import { largeSpacing, halfSpacing } from 'styles/global_defaults/scaffolding';
import t from 'react-translate';
import { RankingBars, RankingBarsDataUnit } from './ranking-bars';

type StateProps = {
  // TODO: rename this Top10CoursesRanking type instead of aliasing it here
  courseRanking: CourseRankingData,
  isLoaded: boolean,
  institutionId: number,
  coursesAreLoaded: boolean,
};

const mapDispatchToProps = {
  getTopTenCourses,
  loadCoursesForInstitution,
};

type OwnProps = {
  startDate: moment.Moment,
  endDate: moment.Moment,
  dateText: string,
  periodType: EnrollmentPeriod,
};

const TopEnrolledPanel = (props: StateProps & typeof mapDispatchToProps & OwnProps) => {
  const style = css`
    display: flex;
    flex-direction: column;
    justify-content: center;

    .date-text {
      margin-top: ${halfSpacing}px;
      margin-bottom: ${largeSpacing}px;
    }
  `;

  useEffect(() => {
    // The start date and end date props will be null if the license data
    // hasn't finished loading
    if (!props.startDate || !props.startDate.isValid() || !props.endDate || !props.endDate.isValid() || !props.institutionId) {
      return;
    }

    // Load the course data if not already done, and then load the course
    // rankins on re-render once the course data is ready
    // TODO: This is hacky. We should be handling this data dependency either
    // a) not on component render but outside of it, or
    // b) sequenced in a saga iterator
    if (!props.coursesAreLoaded) {
      props.loadCoursesForInstitution({ institutionId: props.institutionId });
    } else {
      props.getTopTenCourses({
        startTime: props.startDate.toISOString(),
        endTime: props.endDate.toISOString(),
        institutionId: props.institutionId,
      });
    }
  }, [props.startDate.format(), props.endDate.format(), props.coursesAreLoaded, props.institutionId]);

  const data: RankingBarsDataUnit[] = props.courseRanking ? props.courseRanking.map(cr => ({
    title: cr.name,
    catalogId: cr.catalogId,
    actual: cr.loginCount,
    total: cr.enrollmentCount,
  })) : [];

  // See enrollment-panel.tsx. We have to hide the login %'s and % bars in this case since the
  // backend doesn't store historical login data
  const hideLogins = props.endDate.isBefore(moment(), 'day');

  if (props.courseRanking !== null && props.courseRanking.length === 0) {
    return <div />;
  }

  function getDateText() {
    switch (props.periodType) {
      case EnrollmentPeriod.CUSTOM_DATE_RANGE:
        return t.INSTITUTIONS.LICENSES.DASHBOARD.FROM_TO(props.endDate.format('MM/DD/YYYY'), props.startDate.format('MM/DD/YYYY'));
      default:
        return props.dateText;
    }
  }

  return (
    <div css={style}>
      <div className='text-xl text-center font-weight-light'>{t.INSTITUTIONS.LICENSES.DASHBOARD.TOP_ENROLLED()}</div>
      <div className='date-text text-center text-medium condensed font-weight-bold'>{getDateText()}</div>
      <LoadingWrapper isLoaded={props.courseRanking !== null}>
        <div className='mx-auto'>
          <RankingBars
            data={data}
            activeLabelTranslation={t.INSTITUTIONS.LICENSES.DASHBOARD.PCT_LOGGED_IN}
            hideLogins={hideLogins}
          />
        </div>
      </LoadingWrapper>
    </div>
  );
};

export default connect<StateProps, typeof mapDispatchToProps, OwnProps, RootState>(
  (state, ownProps) => ({
    courseRanking: getCourseNamesToRankingData(state),
    isLoaded: state.models.license.loaded,
    institutionId: state.app.currentInstitutionId,
    coursesAreLoaded: state.app.licenseDashboard.allCoursesLoaded,
    ...ownProps,
  }),
  mapDispatchToProps as any,
)(TopEnrolledPanel);
